import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  QrCode, 
  Send, 
  RefreshCw, 
  ArrowLeft, 
  MessageSquare, 
  Phone,
  Wifi,
  WifiOff,
  Clock,
  User,
  Smartphone
} from 'lucide-react';
import toast from 'react-hot-toast';

const InstanceManager = ({ user, onLogout }) => {
  const { instanceName } = useParams();
  const navigate = useNavigate();
  const [instance, setInstance] = useState(null);
  const [qrCode, setQrCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [qrLoading, setQrLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (instanceName) {
      loadInstance();
      
      // Actualizar estado cada 10 segundos
      const interval = setInterval(() => {
        loadInstance();
      }, 10000);
      
      return () => clearInterval(interval);
    }
  }, [instanceName]);

  const loadInstance = async () => {
    try {
      setLoading(true);
      console.log(`🔍 Cargando información de instancia: ${instanceName}`);
      
      const response = await axios.get(`/api/instances/${instanceName}/status`);
      setInstance(response.data.instance);
      
      console.log(`📊 Estado de ${instanceName}:`, response.data.instance.status);
    } catch (error) {
      console.error('❌ Error al cargar instancia:', error);
      toast.error('Error al cargar la instancia');
    } finally {
      setLoading(false);
    }
  };

  const generateQR = async () => {
    try {
      setQrLoading(true);
      setQrCode(''); // Limpiar QR anterior
      
      console.log(`🔍 Generando QR para instancia: ${instanceName}`);
      const response = await axios.get(`/api/instances/${instanceName}/qr`);
      
      console.log('📊 Respuesta del servidor:', response.data);
      
      if (response.data.success) {
        if (response.data.qrCode) {
          setQrCode(response.data.qrCode);
          toast.success('QR generado exitosamente');
          
          // Actualizar estado de la instancia
          setInstance(prev => ({
            ...prev,
            status: 'connecting'
          }));
        } else if (response.data.status === 'connected') {
          toast.success('La instancia ya está conectada');
          // Recargar información de la instancia
          loadInstance();
        } else {
          console.error('Respuesta inesperada:', response.data);
          toast.error('Error: Respuesta inesperada del servidor');
        }
      } else {
        console.error('Respuesta de error:', response.data);
        toast.error(response.data.error || 'Error al generar el código QR');
      }
    } catch (error) {
      console.error('❌ Error al generar QR:', error);
      
      if (error.response?.status === 404) {
        toast.error('Instancia no encontrada. Verifica que la instancia existe.');
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Error de conexión al generar el código QR');
      }
    } finally {
      setQrLoading(false);
    }
  };

  const sendTestMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !phoneNumber.trim()) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    try {
      setSending(true);
      console.log(`📤 Enviando mensaje de prueba desde ${instanceName} a ${phoneNumber}`);
      
      await axios.post(`/api/instances/${instanceName}/send`, {
        phoneNumber: phoneNumber.trim(),
        message: message.trim()
      });
      
      toast.success('Mensaje enviado exitosamente');
      setMessage('');
      setPhoneNumber('');
    } catch (error) {
      console.error('❌ Error al enviar mensaje:', error);
      const errorMessage = error.response?.data?.error || 'Error al enviar el mensaje';
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const restartInstance = async () => {
    try {
      console.log(`🔄 Reiniciando instancia: ${instanceName}`);
      await axios.post(`/api/instances/${instanceName}/restart`);
      toast.success('Instancia reiniciada exitosamente');
      
      // Actualizar estado
      setInstance(prev => ({
        ...prev,
        status: 'restarting'
      }));
      
      // Recargar después de unos segundos
      setTimeout(() => loadInstance(), 3000);
    } catch (error) {
      console.error('❌ Error al reiniciar instancia:', error);
      toast.error('Error al reiniciar instancia');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return '#10b981';
      case 'connecting':
      case 'restarting':
        return '#f59e0b';
      case 'disconnected':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'connected':
        return 'Conectado';
      case 'connecting':
        return 'Conectando...';
      case 'restarting':
        return 'Reiniciando...';
      case 'disconnected':
        return 'Desconectado';
      default:
        return 'Desconocido';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <Wifi size={20} />;
      case 'connecting':
      case 'restarting':
        return <Clock size={20} />;
      case 'disconnected':
        return <WifiOff size={20} />;
      default:
        return <WifiOff size={20} />;
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando información de la instancia...</p>
        </div>
      </div>
    );
  }

  if (!instance) {
    return (
      <div className="dashboard-container">
        <div className="card">
          <div className="empty-state">
            <p>Instancia no encontrada</p>
            <button onClick={() => navigate('/')} className="btn-primary">
              <ArrowLeft size={16} />
              Volver al Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="btn-secondary btn-small"
            >
              <ArrowLeft size={16} />
            </button>
                    <div className="text-left">
                      <img 
                        src="https://waengine.com/wp-content/uploads/WAEngine_logo_transparent_horiz-01.png" 
                        alt="WAEngine" 
                        title="WAEngine" 
                        style={{ maxHeight: '60px', marginBottom: '8px' }}
                      />
                      <h1 style={{ fontSize: '20px', marginBottom: '2px' }}>
                        Instancia: {instanceName}
                      </h1>
                      <p style={{ margin: 0, fontSize: '14px' }}>
                        Gestiona tu instancia de WhatsApp
                      </p>
                    </div>
          </div>
          
          <div className="d-flex align-items-center gap-3">
            <button onClick={loadInstance} className="btn-secondary btn-small">
              <RefreshCw size={16} />
              Actualizar
            </button>
            
            <div className="user-info">
              <div className="text-end">
                <small className="text-muted d-block">Sesión activa</small>
                <span className="user-email">{user?.email}</span>
              </div>
              <button onClick={onLogout} className="logout-btn">
                🚪 Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Estado de la instancia */}
      <div className="card">
        <h2>Estado de la Instancia</h2>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
          <span
            className="status-badge"
            style={{
              backgroundColor: getStatusColor(instance.status),
              padding: '12px 20px',
              borderRadius: '25px',
              fontSize: '16px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {getStatusIcon(instance.status)}
            {getStatusText(instance.status)}
          </span>
          
          <button onClick={restartInstance} className="btn-secondary btn-small">
            <RefreshCw size={16} />
            Reiniciar Instancia
          </button>
        </div>
        
        {instance.phoneNumber && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#666', marginBottom: '10px' }}>
            <Phone size={16} />
            <span><strong>Número:</strong> {instance.phoneNumber}</span>
          </div>
        )}
        
        {instance.profileName && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#666' }}>
            <User size={16} />
            <span><strong>Perfil:</strong> {instance.profileName}</span>
          </div>
        )}
      </div>

      {/* Código QR para Conexión/Reconexión */}
      <div className="card">
        <h2>
          <QrCode size={24} style={{ display: 'inline', marginRight: '8px' }} />
          {instance.status === 'open' ? 'Reconexión' : 'Conexión'} WhatsApp
        </h2>
        <p>
          {instance.status === 'open' 
            ? 'Si necesitas reconectar el dispositivo, genera un nuevo código QR'
            : 'Escanea este código con WhatsApp para conectar tu número'
          }
        </p>
        
        <div className="qr-container">
          {qrLoading ? (
            <div style={{ padding: '40px' }}>
              <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
              <p>Generando código QR...</p>
            </div>
          ) : qrCode ? (
            <div>
              <img 
                src={qrCode} 
                alt="QR Code" 
                className="qr-image"
              />
              <p style={{ marginTop: '10px', color: '#666' }}>
                Escanea este código con WhatsApp
              </p>
              <button 
                onClick={generateQR} 
                className="btn-secondary btn-small"
                style={{ marginTop: '10px' }}
              >
                <RefreshCw size={16} />
                Regenerar QR
              </button>
            </div>
          ) : (
            <div style={{ padding: '40px' }}>
              <Smartphone size={64} style={{ color: '#ccc', marginBottom: '20px' }} />
              <p style={{ color: '#666', marginBottom: '20px' }}>
                {instance.status === 'open' 
                  ? 'Haz clic en "Generar QR" si necesitas reconectar el dispositivo'
                  : 'Haz clic en "Generar QR" para obtener el código de conexión'
                }
              </p>
              <button
                onClick={generateQR}
                className="btn-primary"
                disabled={qrLoading}
              >
                <QrCode size={16} />
                {instance.status === 'open' ? 'Generar QR de Reconexión' : 'Generar Código QR'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Enviar mensaje de prueba - Solo si está conectado */}
      {instance.status === 'open' && (
        <div className="card">
          <h2>
            <MessageSquare size={24} style={{ display: 'inline', marginRight: '8px' }} />
            Enviar Mensaje de Prueba
          </h2>
          <p>Envía un mensaje de prueba para verificar que la instancia funciona correctamente</p>
          
          <form onSubmit={sendTestMessage} className="form" style={{ marginTop: '20px' }}>
            <input
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Número de teléfono (ej: 5491112345678)"
              className="input-field"
              disabled={sending}
            />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Mensaje a enviar"
              className="input-field"
              rows="3"
              disabled={sending}
              style={{ minHeight: '80px', resize: 'vertical' }}
            />
            <button
              type="submit"
              className="btn-primary"
              disabled={sending || !message.trim() || !phoneNumber.trim()}
            >
              {sending ? (
                <>
                  <div className="spinner" style={{ width: '16px', height: '16px', marginRight: '8px' }}></div>
                  Enviando...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Enviar Mensaje
                </>
              )}
            </button>
          </form>
        </div>
      )}

      {/* Información adicional si no está conectado */}
      {instance.status !== 'open' && (
        <div className="card">
          <h2>Instrucciones de Conexión</h2>
          <div style={{ color: '#666', lineHeight: '1.6' }}>
            <p><strong>Para conectar tu WhatsApp:</strong></p>
            <ol style={{ marginLeft: '20px', marginTop: '10px' }}>
              <li>Genera el código QR haciendo clic en el botón de arriba</li>
              <li>Abre WhatsApp en tu teléfono</li>
              <li>Ve a Configuración → Dispositivos vinculados</li>
              <li>Toca "Vincular un dispositivo"</li>
              <li>Escanea el código QR que aparece en esta pantalla</li>
            </ol>
            <p style={{ marginTop: '15px', fontStyle: 'italic' }}>
              Una vez conectado, podrás enviar mensajes de prueba y gestionar la instancia.
            </p>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <div className="text-center mt-4">
        <small style={{ color: 'var(--waengine-text-muted)' }}>
          WAEngine API Manager | Made with ❤️ by InnovateIA
        </small>
      </div>
    </div>
  );
};

export default InstanceManager;
